import PropTypes from "prop-types";
import React, { useEffect, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";
import { FoldersApi } from "../../../api/folders/folders";
import { NO_ITEMS_RESP_ERR, ROUTE_URIS } from "../../../data/StringConstants";
import {
	getQueryParamsFromCurrentHash,
	parseFolderIdFromCurrentHash
} from "../../../utils/routingHelpers";
import ItemsContent from "./ItemsContent";

/**
 * This container is responsible for fetching items. Currently it is coded to take folder details and get items for a specific folder
 */
function ItemsContentContainer({ folderDetails }) {
	let history = useHistory();
	let location = useLocation();

	const initialState = { 
		items: [],
		totalItemCount: 0,
		isLoading: false,
		hasMore: false,
		currentPage: 1
	}

	const [itemsState, setItemsState] = useState(initialState);

	const queryParams = getQueryParamsFromCurrentHash(history.location.hash);
	const { sortBy, creator, format, starred } = queryParams;
	let isCancelled = false;

	useEffect(() => {
		const folderIdInUrl = parseFolderIdFromCurrentHash(history.location.hash);

		//Only fetch data when URL folderId matches the folderId prop
		//The mismatch happens when we are transitioning between two different folders
		//i.e. copying an item from one folder to another, and then redirecting to the new folder
		if (folderDetails.folderId === folderIdInUrl) {
			initialFetch();
		}

		return () => (isCancelled = true);
	}, [folderDetails.correlationId, history.location.hash]);

	/** 
	 * Fetches data for three scenarios:
	 * 1. initial page load
	 * 2. an item is deleted
	 * 3. an item is copied to the same folder
	 * In each of these cases, we need to reset the current page number and items. 
	 * Therefore, we call fetchData with pageNumber = 1 and currentItems = [] by default.
	 * When we do not want to reset the state, we pass in the current page and items.
	 * @param {int} pageNumber: default: 1, which is the first page 
	 * @param {[object]} currentItems: default: [], representing an empty result set 
	 */
	const initialFetch = async (pageNumber = 1, currentItems = []) => {
		fetchData(pageNumber, currentItems);
	};

	const fetchData = async (page, items) => {
		setItemsState({ 
			...itemsState,
			isLoading: true,
			currentPage: page
		})

		try {
			const response = await FoldersApi.fetchActiveItemsForOrg({
				sort: sortBy,
				page: page,
				size: 25,
				contextId: folderDetails.correlationId,
				creatorId: creator,
				formatId: format,
				starred: starred
			});
			// Axios will intrinsically throw if no a 20(n) value
			if (!isCancelled) {
				setItemsState({
					...itemsState,
					items: items.concat(response.data.Data),
					currentPage: response.data.PageNumber,
					hasMore: !response.data.IsLastPage,
					totalItemCount: response.data.TotalItemCount,
					isLoading: false,
				});
			}
		} catch (error) {
			/**
			 * @todo - hook this up to a frontend error monitoriig tool
			 */
			console.log(NO_ITEMS_RESP_ERR, error);
			setItemsState({
				...itemsState,
				isLoading: false
			})
		}
	};

	/**
	 * This is the callback invoked when the infinite scrolling Waypoint is triggered. 
	 */
	const fetchMore = async () => {
		if (itemsState.hasMore) {
			const updatedPage = itemsState.currentPage + 1;

			fetchData(updatedPage, itemsState.items);
		}
	};

	const deleteItemFromState = () => {
		initialFetch();
	};

	/**
	 * @description Refresh the Items collection or Redirect to the appropriate folder
	 * @param {object} payload - Copy Item payload
	 * @param {string} payload.newContext - guid of the promotion/folder the new item belongs to
	 * @param {number} payload.folderId - Promotion/Folder Id (table id, not context)
	 */
	const triggerStateRefresh = payload => {
		if (
			payload.newContext &&
			folderDetails.correlationId === payload.newContext.ContextId
		) {
			initialFetch();
		} else {
			history.push(
				`${location.pathname}${ROUTE_URIS.FOLDERS_ALL_FOLDERS}/${payload.folderId}`
			);
		}
	};

	return (
		<ItemsContent
			queryParams={queryParams}
			items={itemsState.items}
			copyItem={triggerStateRefresh}
			totalItemCount={itemsState.totalItemCount}
			deleteItem={deleteItemFromState}
			history={history}
			fetchMore={fetchMore}
			isLoading={itemsState.isLoading}
		/>
	);
}

ItemsContentContainer.propTypes = {
	folderDetails: PropTypes.shape({
		correlationId: PropTypes.string,
		id: PropTypes.string
	}).isRequired
};

export default ItemsContentContainer;
